import type { AuthAppRole, AuthOrganization } from '@/app/providers/auth-context'
import { ForbiddenError, UnauthorizedError } from '@/lib/api/errors'
import { api } from '@/lib/api/requests'

type OrganizationsResponse = {
  organizations?: Array<{
    orgId?: string
    name?: string
    role?: string
    isCoach?: boolean
    slug?: string | null
  }>
}

type MePlayerResponse = {
  player?: {
    playerId?: string
    displayName?: string
    email?: string
  } | null
  platform_role?: string
}

export function normalizeOrgRole(rawRole: string | undefined): AuthAppRole {
  const role = String(rawRole || '').trim().toLowerCase()
  if (role === 'owner' || role === 'admin') return 'CLUB_ADMIN'
  if (role === 'organizer') return 'COACH'
  return 'PLAYER'
}

export function normalizeOrganizations(rows: OrganizationsResponse['organizations']): AuthOrganization[] {
  if (!Array.isArray(rows)) return []
  const normalized: AuthOrganization[] = []
  rows.forEach((row) => {
    const orgId = String(row?.orgId || '').trim()
    if (!orgId) return
    normalized.push({
      orgId,
      name: String(row?.name || '').trim() || undefined,
      role: String(row?.role || '').trim().toLowerCase() || undefined,
      isCoach: row?.isCoach === true,
      slug: String(row?.slug || '').trim() || null,
    })
  })
  return normalized
}

/**
 * One round-trip for "who is this session": the player record and the clubs it
 * follows, fetched together and normalized. Returns safe defaults instead of
 * throwing — see the failure-isolation note inside.
 */
export async function fetchAuthProfile(): Promise<{
  me: MePlayerResponse | null
  organizations: AuthOrganization[]
}> {
  // Each request swallows ALL errors (auth and non-auth) and falls
  // back to a safe default. A transient /me/player 5xx must NOT
  // wipe out a successful /organizations response, and vice versa
  // — otherwise super admins (whose platform_role comes only from
  // /me/player) would land on /forbidden the moment that request
  // hiccups.
  //
  // Both calls also retry on transient cold-start failures (429/5xx)
  // before the swallow kicks in: without it, a single cold-Lambda
  // hiccup at login yields empty organizations -> the workspace and
  // org pages show "No organizations" until a manual refresh. Mirrors
  // the retry the per-org workspace fetches already use.
  const retryColdStart = { attempts: 2, backoffMs: 200, retryOn: [429, 502, 503, 504] }
  const [meResult, orgResult] = await Promise.all([
    api
      .get<MePlayerResponse>('/me/player', { handleAuthErrors: false, retry: retryColdStart })
      .catch((error: unknown) => {
        if (!(error instanceof UnauthorizedError) && !(error instanceof ForbiddenError)) {
          // eslint-disable-next-line no-console
          console.warn('[auth] /me/player failed; treating as unlinked', error)
        }
        return null
      }),
    api
      .get<OrganizationsResponse>('/organizations', { handleAuthErrors: false, retry: retryColdStart })
      .catch((error: unknown) => {
        if (!(error instanceof UnauthorizedError) && !(error instanceof ForbiddenError)) {
          // eslint-disable-next-line no-console
          console.warn('[auth] /organizations failed; treating as empty', error)
        }
        return { organizations: [] }
      }),
  ])

  return { me: meResult, organizations: normalizeOrganizations(orgResult?.organizations) }
}
